import * as React from 'react';
import type { FormikProps, FormikBag } from 'formik';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { SegmentedControl } from '@digital/react-aviva-atom';

import { PupilFormType } from '../../containers/pupil-details/PupilDetails';
import { ImportantInformation } from '../../components/important-information/ImportantInformation';
import { ScrollToError } from '../scroll-to-error/ScrollToError';
import ThirdPartyFields from './third-party-fields/ThirdPartyFields';
import PupilFields from './pupil-fields/PupilFields';
import { TermsFields } from './terms-fields/TermsFields';
import { validationSchema } from './validationSchema';
import type { RootState } from '../../app/store';

import './CustomerDetailsForm.css';
import { YesNoOptions } from '../../redux/user-selection/userSelection';
import { CardHolderFields } from './card-holder-fields/CardHolderFields';

export interface PayerDetails {
    email: string;
    confirmEmail: string;
    mobile: string;
    addressLine1: string;
    addressLine2: string;
    town: string;
    countyId?: number;
    eircode?: string;
}

export interface PupilValues extends PayerDetails {
    firstName: string;
    surname: string;
    dob: {
        dobDay: string;
        dobMonth: string;
        dobYear: string;
    };
    emergencyNumber: string;
    marketingConsent: boolean;
    termConditionsConsent: boolean;

}

export interface ThirdPartyValues extends PayerDetails {
    firstName?: string;
    surname?: string;
}

export interface PupilFormValues {
    purchaser?: PupilFormType;
    thirdParty?: ThirdPartyValues | null;
    pupil: PupilValues;
    sameDetails?: YesNoOptions;
}

export interface InjectedProps {
    initialValues?: PupilFormValues;
}

export type ConnectedFormProps = CustomerDetailsFormOwnProps & InjectedProps;

export interface CustomerDetailsFormOwnProps {
    onSubmit: (formvalues: PupilFormValues) => void;
}

export type Props = FormikProps<PupilFormValues> & ConnectedFormProps;

export class CustomerDetailsForm extends React.Component<Props> {

    isSubmitDisabled(values: PupilFormValues, isSubmitting: boolean) {
        return isSubmitting
            || !values.pupil.termConditionsConsent;
    }

    render() {
        const { isSubmitting, values, errors, isValid, touched, handleSubmit, setFieldValue } = this.props;
        const formType = values.purchaser;
        const purchaserOptions = [
            { title: 'Myself', value: PupilFormType.Pupil },
            { title: 'Someone else', value: PupilFormType.ThirdParty },
        ];

        const disableSubmit = this.isSubmitDisabled(values, isSubmitting);

        return (
            <form className="customer-details-container" data-testid="customer-details-form" onSubmit={handleSubmit}>
                <div className="m-form-row">
                    <div className="m-form-row__content">
                        <label className="a-label">
                            I am purchasing these lessons for
                        </label>
                        <SegmentedControl
                            options={purchaserOptions}
                            onChange={(val) => {
                                if (val !== values.purchaser) {
                                    setFieldValue('purchaser', val);
                                    if (val === PupilFormType.ThirdParty) {
                                        setFieldValue('sameDetails', YesNoOptions.No);
                                    } else {
                                        setFieldValue('sameDetails', undefined);
                                    }
                                }
                            }}
                            value={values.purchaser}
                            autoFocus={false}
                        />
                    </div>
                </div>
                {formType &&
                    <div>
                        <ScrollToError
                            errors={errors}
                            isSubmitting={isSubmitting}
                            isValid={isValid}
                            render={({ setFieldRef }) => (
                                <div>
                                    <PupilFields
                                        formType={formType}
                                        values={values.pupil}
                                        errors={errors.pupil || {}}
                                        touched={touched.pupil || {}}
                                        setFieldRef={setFieldRef}
                                    />
                                    <CardHolderFields
                                        formType={formType}
                                        setFieldValue={setFieldValue}
                                        sameDetails={values.sameDetails}
                                    />
                                    <ThirdPartyFields
                                        values={values.thirdParty}
                                        formType={formType}
                                        errors={errors.thirdParty || {}}
                                        touched={touched.thirdParty || {}}
                                        setFieldRef={setFieldRef}
                                        setFieldValue={setFieldValue}
                                        sameDetails={values.sameDetails}
                                    />
                                    <TermsFields
                                        values={values.pupil}
                                        formType={formType}
                                        errors={errors.thirdParty || {}}
                                        touched={touched.thirdParty || {}}
                                    />
                                </div>
                            )}
                        />
                        <div className="m-form-row">
                            <ImportantInformation />
                        </div>
                        <div className="m-form-row">
                            <div className="m-form-row__content">
                                <button
                                    type="submit"
                                    className="a-button a-button--primary a-button--next a-button--full-width"
                                    disabled={disableSubmit}
                                >
                                    Continue & Pay
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </form>
        );
    }
}

// hook our form up to formik
export const FomikEnhancedForm = withFormik<ConnectedFormProps, PupilFormValues>({
    mapPropsToValues: props => {
        return {
            ...props.initialValues
        };
    },
    handleSubmit(values: PupilFormValues, formikBag: FormikBag<CustomerDetailsFormOwnProps, PupilFormValues>) {
        if(values.sameDetails === undefined) {
            if(values.purchaser === PupilFormType.Pupil) {
                values.sameDetails = YesNoOptions.Yes;
            }
        }
        formikBag.props.onSubmit(values);
        formikBag.setSubmitting(false);
    },
    validationSchema
})(CustomerDetailsForm);

// hook our form into redux
export default connect<InjectedProps, {}>(mapStateToProps, {})(FomikEnhancedForm);

export function mapStateToProps(state: RootState) {
    return {
        initialValues: state.userSelection.pupilDetails
    };
}